import React, { useState } from 'react';
import { Search, ChevronDown, ChevronRight, BookOpen, MessageCircle, CreditCard, Calendar, Users, Shield } from 'lucide-react';

/**
 * HelpCentre Component
 *
 * Provides comprehensive help documentation and FAQs for users.
 * Organized by categories with searchable content.
 */
const HelpCentre = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState([0]); // First category expanded by default

  const toggleCategory = (index) => {
    setExpandedCategories(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  // Help content organized by category
  const helpCategories = [
    {
      title: 'Getting Started',
      icon: BookOpen,
      color: 'blue',
      questions: [
        {
          q: 'How do I create an account?',
          a: 'Click on "Register" in the navigation bar and choose your account type (Professional or Seeker). Fill in your details and verify your email address to get started.'
        },
        {
          q: 'What\'s the difference between Professional and Seeker accounts?',
          a: 'Professional accounts are for mentors who offer coffee chats and consultations. Seeker accounts are for individuals looking to connect with professionals for guidance and advice. You can switch between roles in your account settings.'
        },
        {
          q: 'How do I set up my profile?',
          a: 'After registration, go to "Public Profile Setup" to add your bio, experience, industry, and university. Professionals should also set up their availability and connect their payment account.'
        }
      ]
    },
    {
      title: 'Coffee Chats & Bookings',
      icon: Calendar,
      color: 'green',
      questions: [
        {
          q: 'How do I book a coffee chat?',
          a: 'Browse professionals in the "Professionals" page, select someone you\'d like to connect with, choose an available time slot, and complete the payment authorization. Once the professional accepts, your chat is confirmed.'
        },
        {
          q: 'What is the standard session duration?',
          a: 'All coffee chat sessions are standardized to 30 minutes to ensure efficient scheduling and fair pricing.'
        },
        {
          q: 'Can I cancel a booking?',
          a: 'Yes, you can cancel up to 24 hours before the scheduled time for a full refund. Cancellations within 24 hours may be subject to a partial refund depending on the professional\'s policy.'
        },
        {
          q: 'How do I join my scheduled meeting?',
          a: 'You\'ll receive an email with the meeting link (Google Meet or Daily.co). You can also access it from your "Coffee Chats" page 15 minutes before the scheduled time.'
        }
      ]
    },
    {
      title: 'Payments & Billing',
      icon: CreditCard,
      color: 'purple',
      questions: [
        {
          q: 'How does payment work?',
          a: 'We use Stripe for secure payment processing. When you book a session, your payment is pre-authorized (held) for up to 7 days. Once the professional accepts or after 3 days, the payment is captured. If declined, you receive a full refund immediately.'
        },
        {
          q: 'What is the platform commission?',
          a: 'Handshake takes a 10% platform fee from each session payment. Professionals receive 90% of the session rate, with payouts processed weekly every Monday at 9 AM GMT.'
        },
        {
          q: 'How do I connect my bank account as a professional?',
          a: 'Go to Settings → Payments → Connect Bank Account. You\'ll be redirected to Stripe Connect to securely link your bank account and complete identity verification.'
        },
        {
          q: 'When do I receive my earnings?',
          a: 'Earnings are automatically paid out weekly on Mondays at 9 AM GMT via Stripe Connect. You can track your earnings and payout history in the "Earnings" page.'
        },
        {
          q: 'What currencies are supported?',
          a: 'We support 10+ currencies including GBP, USD, EUR, CAD, AUD, and more. Rates are converted in real-time using current exchange rates.'
        }
      ]
    },
    {
      title: 'Messaging',
      icon: MessageCircle,
      color: 'yellow',
      questions: [
        {
          q: 'How do I send messages?',
          a: 'Once you\'ve booked a session and payment is authorized, the chat thread with the professional is unlocked. Navigate to "Messages" to start your conversation.'
        },
        {
          q: 'Can I send messages before booking?',
          a: 'No, you need to book and authorize payment for a session before you can message a professional. This ensures professionals are compensated for their time and expertise.'
        },
        {
          q: 'Are messages real-time?',
          a: 'Yes! We use Socket.IO for real-time messaging. You\'ll see messages instantly without needing to refresh the page.'
        },
        {
          q: 'Can I send files or attachments?',
          a: 'Currently, text messages are supported. File sharing will be available in a future update.'
        }
      ]
    },
    {
      title: 'Account Management',
      icon: Users,
      color: 'red',
      questions: [
        {
          q: 'How do I change my password?',
          a: 'Go to Account Settings → Security and click "Change Password". You\'ll need to enter your current password and choose a new one.'
        },
        {
          q: 'I forgot my password. What should I do?',
          a: 'Click "Forgot Password?" on the login page. Enter your email address and you\'ll receive a password reset link valid for 1 hour.'
        },
        {
          q: 'How do I delete my account?',
          a: 'Go to Account Settings → Account → Delete Account. This will permanently delete your profile, messages, and personal data. Payment records are retained for legal compliance but anonymized.'
        },
        {
          q: 'Can I change my email address?',
          a: 'Email address changes are currently being implemented. Please contact support for assistance.'
        }
      ]
    },
    {
      title: 'Privacy & Security',
      icon: Shield,
      color: 'indigo',
      questions: [
        {
          q: 'How is my payment information protected?',
          a: 'We use Stripe, a PCI-compliant payment processor. We never store your credit card details on our servers. All payment data is encrypted and handled securely by Stripe.'
        },
        {
          q: 'Is my personal data safe?',
          a: 'Yes, we take data protection seriously. All data is encrypted in transit and at rest. We comply with GDPR and other data protection regulations. See our Privacy Policy for details.'
        },
        {
          q: 'Who can see my profile?',
          a: 'Your public profile (bio, industry, university) is visible to all logged-in users. Your email address, payment information, and private messages are never shared.'
        }
      ]
    }
  ];

  // Filter questions based on search query
  const filteredCategories = helpCategories.map(category => ({
    ...category,
    questions: category.questions.filter(
      item =>
        item.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const colorClasses = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Help Centre
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Find answers to common questions and learn how to make the most of Handshake
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search for help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-4">
          {filteredCategories.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-gray-500 dark:text-gray-400">
                No results found for "{searchQuery}". Try different keywords.
              </p>
            </div>
          ) : (
            filteredCategories.map((category, categoryIndex) => {
              const Icon = category.icon;
              const isExpanded = expandedCategories.includes(categoryIndex);

              return (
                <div
                  key={categoryIndex}
                  className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                >
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(categoryIndex)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${colorClasses[category.color]}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {category.title}
                      </h2>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ({category.questions.length} {category.questions.length === 1 ? 'question' : 'questions'})
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </button>

                  {/* Questions & Answers */}
                  {isExpanded && (
                    <div className="px-6 pb-4 space-y-4">
                      {category.questions.map((item, qIndex) => (
                        <div key={qIndex} className="border-l-2 border-gray-200 dark:border-gray-700 pl-4">
                          <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                            {item.q}
                          </h3>
                          <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                            {item.a}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Contact Support */}
        <div className="mt-12 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Still need help?
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Can't find what you're looking for? Our support team is here to help.
          </p>
          <a
            href="/contact-us"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default HelpCentre;
